/**
 * Access helpers — Phase 15 paywall client.
 *
 * `handlePaywallError(err)` — returns true if the error is an HTTP 402/403
 * with our paywall body; toasts a friendly French message with an "Upgrade"
 * action that navigates to /abonnement.
 *
 * `formatQuotaLine(feature)` — one-liner used for the small counter under
 * form fields (e.g. "3 traductions restantes aujourd'hui").
 */
import { toast } from "sonner";

export function isPaywallError(err) {
    const status = err?.response?.status;
    const code = err?.response?.data?.detail?.code;
    return (status === 402 || status === 403) && code === "paywall";
}

export function paywallDetail(err) {
    return err?.response?.data?.detail || null;
}

/** Toast + optional CTA. Returns true if handled, so callers can early-return. */
export function handlePaywallError(err, { onUpgrade } = {}) {
    if (!isPaywallError(err)) return false;
    const detail = paywallDetail(err);
    const message = detail?.message || "Cette fonctionnalité nécessite un abonnement.";
    toast.error(message, {
        duration: 6000,
        action: {
            label: "S'abonner",
            onClick: () => {
                if (onUpgrade) onUpgrade();
                else window.location.assign("/abonnement");
            },
        },
    });
    return true;
}

/** Feature snapshot → human counter line, e.g. "2 traductions restantes aujourd'hui". */
export function formatQuotaLine(feature) {
    if (!feature || feature.unlimited) return null;
    if (feature.window === "paid_only") return "Réservé aux abonnés";
    const { remaining, limit, window } = feature;
    const scope = window === "week" ? "cette semaine" : "aujourd'hui";
    if (remaining <= 0) {
        return `Quota atteint (${limit}/${limit}) ${scope}`;
    }
    return `${remaining} sur ${limit} restant${remaining > 1 ? "es" : "e"} ${scope}`;
}

/** Returns true when the feature quota is exhausted (0 remaining or locked).
 * Callers use this to switch the badge to a warning style and disable the CTA
 * so the paywall is visible BEFORE the click. */
export function isQuotaExhausted(feature) {
    if (!feature || feature.unlimited) return false;
    if (feature.window === "paid_only") return true;
    return (feature.remaining ?? 0) <= 0;
}
